// Timezone-correct "wall clock" for Prague, with no external dependencies.
//
// Everything about "is this section free right now" is a question about the
// LOCAL Prague wall clock (Europe/Prague, which switches between CET and CEST).
// We derive the local date/time parts from a UTC instant using Intl, so DST is
// handled by the platform's timezone database rather than by us.

const PRAGUE_TZ = 'Europe/Prague'

const _fmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: PRAGUE_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

/**
 * @typedef {Object} PragueParts
 * @property {number} year        Full year, e.g. 2026
 * @property {number} month       1-12
 * @property {number} day         1-31
 * @property {number} hour        0-23 (Prague local)
 * @property {number} minute      0-59
 * @property {number} second      0-59
 * @property {number} isoWeekday  1=Mon .. 7=Sun
 * @property {number} minutesOfDay hour*60+minute (0-1439)
 * @property {Date}   instant     the source UTC instant
 */

/**
 * Break a UTC instant down into Prague local calendar/clock parts.
 * @param {Date} [date] defaults to now
 * @returns {PragueParts}
 */
export function pragueParts(date = new Date()) {
  const parts = {}
  for (const p of _fmt.formatToParts(date)) {
    if (p.type !== 'literal') parts[p.type] = p.value
  }
  const year = Number(parts.year)
  const month = Number(parts.month)
  const day = Number(parts.day)
  let hour = Number(parts.hour) % 24 // h23 can still emit 24 at midnight on some engines
  const minute = Number(parts.minute)
  const second = Number(parts.second)

  // Deterministic ISO weekday from the *local* calendar date (not locale-dependent).
  // getUTCDay: 0=Sun..6=Sat -> ISO 1=Mon..7=Sun
  const isoWeekday = ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7) + 1

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    isoWeekday,
    minutesOfDay: hour * 60 + minute,
    instant: date,
  }
}

/** ISO weekday (1=Mon..7=Sun) for a given Y/M/D, timezone-independent. */
export function isoWeekdayOf(year, month, day) {
  return ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7) + 1
}

/** Advance a {year,month,day} by `n` days (n can be negative). Returns a new object. */
export function addDays({ year, month, day }, n) {
  const d = new Date(Date.UTC(year, month - 1, day + n))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }
}

/** "HH:MM" -> minutes since midnight. "24:00" -> 1440. */
export function hhmmToMinutes(t) {
  if (t === '24:00' || t === '2400') return 1440
  const [h, m] = String(t).split(':').map(Number)
  return h * 60 + (m || 0)
}

/** minutes since midnight -> "HH:MM" (1440 -> "24:00"). */
export function minutesToHhmm(min) {
  if (min >= 1440) return '24:00'
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/**
 * The UTC instant at which the Prague wall clock reads a given local
 * date + minute-of-day. Inverse of pragueParts. Resolves the CET/CEST offset
 * from the platform tz database (two passes handle a DST-boundary miss), so the
 * time-travel plan can reach ANY day — including across a DST switch.
 * @returns {Date}
 */
export function instantFromPragueWall(year, month, day, minutesOfDay) {
  const hh = Math.floor(minutesOfDay / 60)
  const mm = minutesOfDay % 60
  const wanted = Date.UTC(year, month - 1, day, hh, mm)
  const shownAt = (t) => {
    const p = pragueParts(new Date(t))
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
  }
  let instant = wanted - (shownAt(wanted) - wanted) // first pass: subtract the offset
  const err = shownAt(instant) - wanted
  if (err !== 0) instant -= err // second pass: fixes a DST-boundary miss
  return new Date(instant)
}

export const WEEKDAY_SHORT_CS = ['', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']
export const WEEKDAY_SHORT_EN = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
